import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSyncAlt } from "react-icons/fa";
import { localToday } from "../utils/localDate";
import { useReportDateState } from "../hooks/useReportDateAccess";
import { API_ENDPOINTS } from "../config/constants";
import { formatStoredMoney } from "../utils/currency";
import { downloadCSV, downloadExcel, downloadPDF, printThermal, printThermalViaUsb } from "../utils/exportHelpers";
import { buildTransactionSummary, buildTransactionSummaryReceiptText } from "../utils/transactionSummaryReport";
import ReportPageHeader from "../components/ReportPageHeader";
import ReportPeriodPicker from "../components/ReportPeriodPicker";
import ReportControlBar from "../components/ReportControlBar";

const toDisplayDate = (value) => {
  const [year, month, day] = String(value || "").split("-");
  return year && month && day ? `${day}-${month}-${year}` : String(value || "");
};

const toDisplayTime = (value) => {
  const [hourRaw, minute = "00"] = String(value || "00:00").split(":");
  const hour = Number(hourRaw);
  return `${String(hour % 12 || 12).padStart(2, "0")}:${minute} ${hour >= 12 ? "PM" : "AM"}`;
};

const orderDateValue = (order) =>
  order?.created_at ?? order?.createdAt ?? order?.date ?? order?.order_date ?? "";

const inSelectedPeriod = (order, fromDate, fromTime, toDate, toTime) => {
  const raw = orderDateValue(order);
  if (!raw) return true;
  const timestamp = new Date(raw).getTime();
  if (!Number.isFinite(timestamp)) return true;
  const from = new Date(`${fromDate}T${fromTime}:00+05:30`).getTime();
  const to = new Date(`${toDate}T${toTime}:59+05:30`).getTime();
  return timestamp >= from && timestamp <= to;
};

const TransactionSummaryReportPage = () => {
  const [filters, setFilters] = useReportDateState(
    { fromDate: localToday(), toDate: localToday() },
    "filters"
  );
  const { fromDate, toDate } = filters;
  const [fromTime, setFromTime] = useState("00:00");
  const [toTime, setToTime] = useState("23:59");
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [, setSourceMessage] = useState("");
  const reportRef = useRef(null);
  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("userData") || "{}")?.data || {}; }
    catch { return {}; }
  }, []);

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, fromTime, toTime]);

  const fetchReport = async (options = {}) => {
    if (!user?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    options.refresh ? setRefreshing(true) : setLoading(true);
    setSourceMessage("");
    try {
      const response = await fetch(API_ENDPOINTS.GET_ORDER_LIST, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ restaurantid: user.id, fromdate: fromDate, todate: toDate }),
      });
      const result = await response.json();
      if (!response.ok || result?.success === false) throw new Error(result?.message || "Unable to load orders");
      const list = Array.isArray(result?.list) ? result.list : [];
      setOrders(list.filter((order) => inSelectedPeriod(order, fromDate, fromTime, toDate, toTime)));
      if (response.headers.get("x-billsarthi-desktop-cache") === "1") {
        setSourceMessage("Offline mode — summary is calculated from saved orders.");
      }
    } catch (error) {
      if (window.desktopOrders) {
        try {
          const cached = await window.desktopOrders.list({ restaurantId: user.id });
          setOrders((cached || []).filter((order) => inSelectedPeriod(order, fromDate, fromTime, toDate, toTime)));
          setSourceMessage("Offline mode — summary is calculated from saved SQLite orders.");
        } catch {
          toast.error(error?.message || "Unable to load summary report");
          setOrders([]);
        }
      } else {
        toast.error(error?.message || "Unable to load summary report");
        setOrders([]);
      }
    } finally {
      options.refresh ? setRefreshing(false) : setLoading(false);
    }
  };

  const summary = useMemo(() => buildTransactionSummary(orders), [orders]);
  const fmtMoney = (value) => formatStoredMoney(value, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  const periodLines = [
    `From ${toDisplayDate(fromDate)} ${toDisplayTime(fromTime)}`,
    `to ${toDisplayDate(toDate)} ${toDisplayTime(toTime)}`,
  ];
  const receiptBuilder = (lineWidth) => buildTransactionSummaryReceiptText({
    restaurant: user.restaurant_name || user.restaurantname || user.name || "Bill Sarthi",
    periodLines,
    printedAt: new Date(),
    summary,
  }, lineWidth);

  const exportRows = [
    ...summary.transactionRows.map((row) => ["Transaction Details", row.label, row.value]),
    ...summary.orderRows.map((row) => ["Order Details", row.label, row.value]),
  ];
  const fileName = `transaction_summary_${fromDate}_${fromTime.replace(":", "-")}_to_${toDate}_${toTime.replace(":", "-")}`;

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />
      <ReportPageHeader
        title="Transaction Summary Report"
        subtitle="Payments, sales, taxes, discounts, guests and order-channel totals in one report."
        stats={[
          { label: "Total Sale", value: fmtMoney(summary.totalSale) },
          { label: "Total Orders", value: summary.totalOrders },
          { label: "Total Qty", value: summary.totalQty },
          { label: "Total Pax", value: summary.totalPax },
        ]}
      />

      <ReportControlBar>
        <ReportPeriodPicker
          fromDate={fromDate}
          toDate={toDate}
          onChange={({ from, to }) => setFilters({ fromDate: from, toDate: to })}
        />
        <TimeField>From time<input type="time" value={fromTime} onChange={(event) => setFromTime(event.target.value)} /></TimeField>
        <TimeField>To time<input type="time" value={toTime} onChange={(event) => setToTime(event.target.value)} /></TimeField>
        <PrimaryButton onClick={() => fetchReport({ refresh: true })}><FaSyncAlt /> {refreshing ? "Refreshing…" : "Refresh"}</PrimaryButton>
      </ReportControlBar>


      <ReportCard ref={reportRef}>
        <ReceiptHeader>
          <strong>{user.restaurant_name || user.restaurantname || user.name || "Bill Sarthi"}</strong>
          <h2>Transaction Summary</h2>
          <span>{periodLines[0]}</span><span>{periodLines[1]}</span>
        </ReceiptHeader>
        {loading ? <Empty>Loading transaction summary…</Empty> : (
          <SummaryGrid>
            <SummarySection title="Transaction Details" rows={summary.transactionRows} moneyFormatter={fmtMoney} />
            <SummarySection title="Order Details" rows={summary.orderRows} />
          </SummaryGrid>
        )}
      </ReportCard>

      <Actions>
        <PrimaryButton onClick={() => downloadExcel(`${fileName}.xlsx`, ["Section", "Particular", "Value"], exportRows)}>Excel</PrimaryButton>
        <PrimaryButton onClick={() => downloadCSV(`${fileName}.csv`, ["Section", "Particular", "Value"], exportRows)}>CSV</PrimaryButton>
        <PrimaryButton onClick={() => downloadPDF(reportRef.current, `${fileName}.pdf`)}>PDF</PrimaryButton>
        <PrimaryButton onClick={() => printThermal(reportRef.current, "Transaction Summary", { buildReceiptText: receiptBuilder })}>Thermal Print</PrimaryButton>
        <PrimaryButton onClick={() => printThermalViaUsb(reportRef.current, "Transaction Summary", { buildReceiptText: receiptBuilder })}>Thermal Print via USB</PrimaryButton>
      </Actions>
    </Page>
  );
};

const SummarySection = ({ title, rows, moneyFormatter }) => (
  <Section>
    <SectionTitle>{title}</SectionTitle>
    <Table>
      <thead><tr><th>Particular</th><th>{moneyFormatter ? "Amount" : "Orders"}</th></tr></thead>
      <tbody>{rows.map((row) => (
        <tr key={row.label}><td>{row.label}</td><td>{moneyFormatter && !row.numeric ? moneyFormatter(row.value) : row.value}</td></tr>
      ))}</tbody>
    </Table>
  </Section>
);

export default TransactionSummaryReportPage;

const Page = styled.main`min-height:100vh;padding:18px;background:radial-gradient(circle at 20% 20%,#f5f8ff,#eef2ff 55%,#f5f7fb 100%);`;
const TimeField = styled.label`display:grid;gap:4px;color:#475569;font-size:11px;font-weight:800;input{height:36px;border:1px solid #cbd5e1;border-radius:9px;padding:0 9px;background:#fff;color:#0f172a;}`;
const PrimaryButton = styled.button`min-height:38px;padding:8px 14px;border:0;border-radius:10px;background:linear-gradient(135deg,var(--app-primary),var(--app-primary-hover));color:#fff;font-weight:800;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;gap:6px;`;
const ReportCard = styled.section`padding:18px;border:1px solid #e2e8f0;border-radius:18px;background:#fff;box-shadow:0 10px 25px rgba(15,23,42,.06);`;
const ReceiptHeader = styled.header`display:grid;justify-items:center;gap:4px;margin-bottom:16px;text-align:center;color:#0f172a;strong{font-size:18px;}h2{margin:8px 0 2px;font-size:20px;}span{color:#64748b;font-size:13px;}`;
const SummaryGrid = styled.div`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:18px;@media(max-width:800px){grid-template-columns:1fr;}`;
const Section = styled.section`overflow:hidden;border:1px solid #dbe4ee;border-radius:14px;`;
const SectionTitle = styled.h3`margin:0;padding:12px;text-align:center;background:#eef6ff;color:#0f4f7d;font-size:15px;`;
const Table = styled.table`width:100%;border-collapse:collapse;th,td{padding:9px 12px;border-bottom:1px solid #e2e8f0;}th{background:#f8fafc;text-align:left;color:#475569;font-size:11px;text-transform:uppercase;}th:last-child,td:last-child{text-align:right;font-variant-numeric:tabular-nums;}tbody tr:last-child td{font-weight:900;background:#f8fafc;border-bottom:0;}`;
const Empty = styled.div`padding:28px;text-align:center;color:#64748b;font-weight:700;`;
const Actions = styled.div`display:flex;flex-wrap:wrap;gap:8px;margin-top:14px;`;
